"use client"

import { useState, useEffect } from "react"
import { Card, CardContent } from "@/components/ui/card"
import { Star, ExternalLink, ChevronDown } from "lucide-react"
import { Link } from "@/i18n/routing"
import { Review } from "@/types/reviews"
import { ReviewCard } from "./review-card"
import { ReviewStars } from "@/components/pages/home/reviews/review-stars"
import { useTranslations, useLocale } from "next-intl"

interface ReviewsData {
  reviews: Review[]
  overallRating: number
  totalReviews: number
}

interface ReviewsClientProps {
  googleBusinessUrl?: string
  googleBusinessReviewsUrl?: string
}

export function ReviewsClient({
  googleBusinessUrl = 'https://maps.app.goo.gl/4TDZN9VyvYB3s5kYA',
  googleBusinessReviewsUrl = 'https://g.page/r/CQFd4LhR8RNqEAE/review',
}: ReviewsClientProps) {
  const t = useTranslations("page.homepage.reviews")
  const locale = useLocale()
  const [data, setData] = useState<ReviewsData | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(false)
  const [displayedCount, setDisplayedCount] = useState(5)

  useEffect(() => {
    async function loadReviews() {
      try {
        const response = await fetch(`/api/reviews?lang=${locale}`)
        if (!response.ok) {
          throw new Error('Failed to fetch reviews')
        }
        const reviewsData = await response.json()
        setData(reviewsData)
      } catch (err) {
        console.error('Error loading reviews:', err)
        setError(true)
      } finally {
        setLoading(false)
      }
    }

    loadReviews()
  }, [locale])

  const handleShowMore = () => {
    setDisplayedCount(prev => prev + 5)
  }

  // Don't render if loading, error, or no data
  if (loading || error || !data) {
    return null
  }

  const { reviews, overallRating, totalReviews } = data
  const fiveStarReviews = reviews.filter((review) => review.rating === 5)
  const displayedReviews = fiveStarReviews.slice(0, displayedCount)
  const hasMoreReviews = displayedCount < fiveStarReviews.length

  return (
    <div className="mx-auto max-w-6xl py-12">
      <div className="text-center mb-8">
        <h3 className="text-2xl font-bold text-teal-900 mb-4">{t("title")}</h3>
        <div className="flex items-center justify-center gap-2 mb-4">
          <ReviewStars rating={overallRating} size="lg" />
          <span className="text-lg font-semibold text-gray-900">{overallRating.toFixed(1)}</span>
          <span className="text-gray-600">{t("count", { count: totalReviews })}</span>
        </div>
        <div className="flex gap-4 justify-center">
          <Link 
            href={googleBusinessReviewsUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center justify-center bg-teal-600 text-white hover:bg-teal-700 px-4 py-2 rounded-md"
          >
            {t("leave_review")}
            <ExternalLink className="ms-2 h-4 w-4" />
          </Link>
          <Link 
            href={googleBusinessUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center justify-center border border-teal-600 text-teal-600 hover:bg-teal-50 px-4 py-2 rounded-md"
          >
            {t("view_all_reviews")}
            <ExternalLink className="ms-2 h-4 w-4" />
          </Link>
        </div>
      </div>

      <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
        {displayedReviews.map((review) => (
          <ReviewCard key={`${review.id}-${review.author}`} review={review} />
        ))}

        {/* Call to action card */}
        <Card className="border-teal-200 bg-teal-50">
          <CardContent className="p-6 text-center">
            <div className="mb-4">
              <Star className="h-8 w-8 text-teal-600 mx-auto mb-2" />
              <h4 className="font-semibold text-teal-900 mb-2">{t("cta_title")}</h4>
              <p className="text-sm text-teal-700 mb-4">
                {t("cta_description")}
              </p>
            </div>
            <Link 
              href={googleBusinessReviewsUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="w-full inline-flex items-center justify-center bg-teal-600 hover:bg-teal-700 text-white px-4 py-2 rounded-md"
            >
              {t("leave_review")}
              <ExternalLink className="ms-2 h-4 w-4" />
            </Link>
          </CardContent>
        </Card>
      </div>

      {hasMoreReviews && (
        <div className="text-center mt-8">
          <button
            onClick={handleShowMore}
            className="inline-flex items-center justify-center gap-2 bg-teal-50 text-teal-600 hover:bg-teal-100 px-6 py-3 rounded-md font-medium transition-colors"
          >
            {t("show_more", { count: 5 })}
            <ChevronDown className="h-4 w-4" />
          </button>
        </div>
      )}
    </div>
  )
} 
